import { useEffect } from 'react';
import identity from '../data/siteIdentity.json';
import { canonicalUrl, normalizeSchemaUrls } from '../utils/seoUrls';

const SITE_URL = identity.url;
const DEFAULT_IMAGE = `${SITE_URL}/ubotika-logo.png`;

function setMetaAttribute(selector, attribute, value) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    const nameMatch = selector.match(/meta\[name="([^"]+)"\]/);
    const propertyMatch = selector.match(/meta\[property="([^"]+)"\]/);
    if (nameMatch) element.setAttribute('name', nameMatch[1]);
    if (propertyMatch) element.setAttribute('property', propertyMatch[1]);
    document.head.appendChild(element);
  }
  element.setAttribute(attribute, value);
}

function setLink(rel, href) {
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
}

function SEO({
  title,
  description,
  path = '/',
  image = DEFAULT_IMAGE,
  type = 'website',
  schema = [],
  noindex = false,
  nofollow = false,
}) {
  useEffect(() => {
    // nginx serves every route in its trailing-slash form and 301s the bare form, so the
    // canonical must carry the slash — otherwise every page self-canonicalises to a redirect.
    const canonical = canonicalUrl(path);

    document.title = title;
    setMetaAttribute('meta[name="description"]', 'content', description);
    setMetaAttribute('meta[name="robots"]', 'content', noindex ? (nofollow ? 'noindex, nofollow' : 'noindex, follow') : 'index, follow, max-image-preview:large');
    setLink('canonical', canonical);

    setMetaAttribute('meta[property="og:type"]', 'content', type);
    setMetaAttribute('meta[property="og:url"]', 'content', canonical);
    setMetaAttribute('meta[property="og:title"]', 'content', title);
    setMetaAttribute('meta[property="og:description"]', 'content', description);
    setMetaAttribute('meta[property="og:image"]', 'content', image);
    setMetaAttribute('meta[property="og:image:alt"]', 'content', title);
    setMetaAttribute('meta[property="og:locale"]', 'content', 'en_US');

    setMetaAttribute('meta[property="twitter:card"]', 'content', 'summary_large_image');
    setMetaAttribute('meta[property="twitter:url"]', 'content', canonical);
    setMetaAttribute('meta[property="twitter:title"]', 'content', title);
    setMetaAttribute('meta[property="twitter:description"]', 'content', description);
    setMetaAttribute('meta[property="twitter:image"]', 'content', image);

    document.head
      .querySelectorAll('script[data-seo-schema="true"]')
      .forEach((element) => element.remove());

    const schemaItems = Array.isArray(schema) ? schema : [schema];
    const webPage = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${canonical}#webpage`,
      url: canonical,
      name: title,
      description,
      inLanguage: 'en-US',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      about: { '@id': `${SITE_URL}/#organization` },
    };
    [...schemaItems, ...(!noindex ? [webPage] : [])].filter(Boolean).forEach((item) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.dataset.seoSchema = 'true';
      script.textContent = JSON.stringify(normalizeSchemaUrls(item)).replace(/</g, '\\u003c');
      document.head.appendChild(script);
    });
  }, [title, description, path, image, type, schema, noindex, nofollow]);

  return null;
}

export { SITE_URL, DEFAULT_IMAGE };
export default SEO;
